import { DashboardScrollView } from '@/components/dashboard-scroll-view';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ThemedText } from '@/components/themed-text';
import { useAuthStore } from '@/store/authStore';
import { Ionicons } from '@expo/vector-icons';
import { router, Stack } from 'expo-router';
import React, { useState } from 'react';
import { View, TextInput, ActivityIndicator } from 'react-native';
import MaskInput from 'react-native-mask-input';
import { hasProviderProfile } from '@/utils/user-role';
import { brazilPhoneMask, getBrazilPhoneError } from '@/utils/brazil-phone';

export default function BecomeProviderScreen() {
  const { user, becomeProvider } = useAuthStore();
  const [title, setTitle] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  const [bio, setBio] = useState('');
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);

  const businessPhoneDigits = businessPhone.replace(/\D/g, '');
  // Mesma regra da API: DDD existente e celular — é o número que vai receber
  // WhatsApp dos clientes.
  const businessPhoneError = getBrazilPhoneError(businessPhone);
  const canSubmit = title.trim().length >= 2 && !businessPhoneError;

  const handleSubmit = async () => {
    if (!canSubmit || loading) return;
    setLoading(true);
    try {
      await becomeProvider({
        title: title.trim(),
        businessPhone:
          businessPhoneDigits.length >= 10 ? businessPhoneDigits : businessPhone.trim(),
        bio: bio.trim() || undefined,
        address: address.trim() || undefined,
      });
      // becomeProvider already navigates via navigateAfterAuth
    } finally {
      setLoading(false);
    }
  };

  // Se já é prestador, redirecionar
  if (user && hasProviderProfile(user)) {
    router.replace('/(tabs)/(provider)/home');
    return null;
  }

  return (
    <View className="flex-1 bg-canvas">
      <Stack.Screen
        options={{
          title: 'Virar prestador',
          headerBackTitle: 'Voltar',
        }}
      />
      <View className="flex-1">
        <DashboardScrollView
          contentContainerStyle={{ padding: 20, paddingBottom: 40 }}
          keyboardShouldPersistTaps="handled"
        >
          <Card className="mb-6">
            <CardContent className="p-4">
              <View className="mb-4 flex-row items-center gap-3">
                <View className="h-12 w-12 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
                  <Ionicons
                    className="text-primary dark:text-white"
                    name="briefcase-outline"
                    size={24}
                  />
                </View>
                <View className="flex-1">
                  <ThemedText className="font-semibold text-lg text-foreground">
                    Seja também um prestador
                  </ThemedText>
                  <ThemedText className="text-sm text-foreground/60">
                    Você manterá todos os seus contratos atuais como cliente. Preencha os dados do
                    seu negócio para começar a oferecer serviços.
                  </ThemedText>
                </View>
              </View>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <ThemedText className="mb-1 text-xs text-foreground/60">
                Nome do negócio ou título *
              </ThemedText>
              <TextInput
                value={title}
                onChangeText={setTitle}
                placeholder="Ex: Consultoria XYZ"
                placeholderTextColor="#a1a1aa"
                className="mb-4 rounded-lg border border-border bg-background px-4 py-3 text-base text-foreground"
                editable={!loading}
              />

              <ThemedText className="mb-1 text-xs text-foreground/60">
                Telefone comercial *
              </ThemedText>
              <MaskInput
                value={businessPhone}
                onChangeText={(masked) => setBusinessPhone(masked)}
                mask={brazilPhoneMask}
                placeholder="(11) 99999-9999"
                placeholderTextColor="#a1a1aa"
                keyboardType="phone-pad"
                className="mb-4 rounded-lg border border-border bg-background px-4 py-3 text-base text-foreground"
                editable={!loading}
              />

              {!!businessPhoneError && businessPhoneDigits.length >= 10 && (
                <ThemedText className="-mt-3 mb-4 text-xs text-red-600">
                  {businessPhoneError}
                </ThemedText>
              )}

              <ThemedText className="mb-1 text-xs text-foreground/60">Sobre (opcional)</ThemedText>
              <TextInput
                value={bio}
                onChangeText={setBio}
                placeholder="Breve descrição do seu negócio"
                placeholderTextColor="#a1a1aa"
                multiline
                numberOfLines={3}
                className="mb-4 rounded-lg border border-border bg-background px-4 py-3 text-base text-foreground"
                editable={!loading}
              />

              <ThemedText className="mb-1 text-xs text-foreground/60">
                Endereço (opcional)
              </ThemedText>
              <TextInput
                value={address}
                onChangeText={setAddress}
                placeholder="Endereço do seu negócio"
                placeholderTextColor="#a1a1aa"
                className="mb-6 rounded-lg border border-border bg-background px-4 py-3 text-base text-foreground"
                editable={!loading}
              />

              <Button onPress={handleSubmit} disabled={!canSubmit || loading} className="w-full">
                {loading ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <View className="flex-row items-center gap-2">
                    <Ionicons name="checkmark-circle-outline" size={20} color="#fff" />
                    <ThemedText className="font-medium text-white">
                      Confirmar e virar prestador
                    </ThemedText>
                  </View>
                )}
              </Button>
            </CardContent>
          </Card>
        </DashboardScrollView>
      </View>
    </View>
  );
}
